import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { Session } from './utils/session';
import { parseDateOnly, toInputDate, toShortDisplayDate, iterateDatesInRange } from '../utils/dateOnly';
import { parseApiDateTimeMs } from '../utils/istTime';
import { buildFirstLoginStartMap, buildLoginTimeMap, capTimeMapToLogin, formatDurationMs, formatLoginStartTime, getEarliestLoginOnDateAfter7Am, getReportFetchRange, hmsToMs, isOnOrAfter7AmIst } from '../utils/loginTime';
import { exportActivityTableToPdf } from '../utils/exportActivityPdf';
import { downloadCsv, downloadExcel, stampFileDate } from '../utils/exportCsv';
import { filterByEmployeeName, matchesEmployeeName } from '../utils/employeeFilter';
import { computeRowProductivityMs } from '../utils/activityTime';
import { buildActiveCallMeetingTaskTimeMap, buildClosedCallMeetingTaskTimeMap, buildFirstTaskStartMap, buildTaskTimeMap, sumUserTaskMinutesForDisplay } from '../utils/taskTime';
import UserLoginHistoryModal from './UserLoginHistoryModal';
import ProductivityRatioBar from './charts/ProductivityRatioBar';
import UserAvatar from './UserAvatar';
import { fetchUsersListCached } from '../utils/usersListCache';
import PageLoader from './PageLoader';

function getLoginDateRange(mode, startDate, endDate, workDate) {
  const rowDate = toInputDate(workDate);
  const fallback = toInputDate(new Date());
  const { fromDate, toDate } = getReportFetchRange(mode, startDate, endDate);

  if (mode === 'daily') {
    const d = rowDate || toDate || fallback;
    return { startDate: d, endDate: d };
  }

  return { startDate: fromDate, endDate: toDate };
}

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${Session.getToken()}`,
});

export default function ActivityTable({
  mode,
  WorkspaceID,
  employeeName,
  startDate,
  endDate,
  refreshKey = 0,
  onReportRows,
  onLoadingChange,
}) {
  const [rows, setRows] = useState([]);
  const [startStopRecords, setStartStopRecords] = useState([]);
  const [userLogs, setUserLogs] = useState([]);
  const [travellingUserIds, setTravellingUserIds] = useState(() => new Set());
  const [usersById, setUsersById] = useState(() => new Map());
  const [usernameById, setUsernameById] = useState(() => new Map());
  const [firstLoginMap, setFirstLoginMap] = useState(new Map());
  const [frozenNow, setFrozenNow] = useState(() => new Date());
  const [enrichmentReady, setEnrichmentReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loginModal, setLoginModal] = useState(null);
  const navigate = useNavigate();
  const meetingNeutralLockRef = useRef(new Map());
  const wasCallMeetingActiveRef = useRef(new Map());
  const neutralMaxRef = useRef(new Map());

  useEffect(() => {
    const controller = new AbortController();

    const buildActivityUrl = () => {
      const endpoint = mode === 'daily' ? 'daily' : 'weekly';
      let url = `${API_BASE_URL}/api/UserActivityReport/${endpoint}`;
      const params = new URLSearchParams();

      if (WorkspaceID) params.append('workspaceId', WorkspaceID);
      if (employeeName?.trim()) params.append('employeeName', employeeName.trim());

      const { fromDate, toDate } = getReportFetchRange(mode, startDate, endDate);
      if (mode === 'daily') {
        params.append('date', fromDate);
      } else {
        params.append('fromDate', fromDate);
        params.append('toDate', toDate);
      }

      url += `?${params.toString()}`;

      return url;
    };

    const loadData = async (showLoading = true) => {
      try {
        if (showLoading) {
          setLoading(true);
          setEnrichmentReady(false);
          setError(null);
          // Clear previous day so stale members never flash for the new filter
          setRows([]);
          setUserLogs([]);
          setStartStopRecords([]);
          setFirstLoginMap(new Map());
          onLoadingChange?.(true);
        }

        const { fromDate, toDate } = getReportFetchRange(mode, startDate, endDate);
        const userLogsUrl = `${API_BASE_URL}/api/Auth/userlogs?fromDate=${encodeURIComponent(fromDate)}&toDate=${encodeURIComponent(toDate)}`;

        const activityPromise = fetch(buildActivityUrl(), {
          method: 'GET',
          headers: authHeaders(),
          signal: controller.signal,
        });

        const usersPromise = fetchUsersListCached({ signal: controller.signal });

        const activityRes = await activityPromise;

        if (activityRes.status === 401) {
          throw new Error('Unauthorized (401)');
        }

        if (!activityRes.ok) {
          throw new Error(`Request failed with status ${activityRes.status}`);
        }

        const [data, usersData] = await Promise.all([
          activityRes.json(),
          usersPromise.catch(() => []),
        ]);

        const filteredRows = filterByEmployeeName(data, employeeName).filter((r) =>
          hmsToMs(r.totalTime ?? r.TotalTime) > 0
          || hmsToMs(r.productive ?? r.Productive) > 0
          || hmsToMs(r.neutral ?? r.Neutral) > 0
          || hmsToMs(r.clickProductive ?? r.ClickProductive) > 0
        );
        setRows(filteredRows);

        const travellingIds = new Set();
        const nameMap = new Map();
        const unameMap = new Map();
        (Array.isArray(usersData) ? usersData : []).forEach((u) => {
          const id = Number(u.userId ?? u.UserId);
          if (id <= 0) return;
          const userName = u.userName ?? u.UserName ?? '';
          const first = u.firstName ?? u.FirstName ?? '';
          const last = u.lastName ?? u.LastName ?? '';
          const fullName = `${first} ${last}`.trim();
          nameMap.set(id, fullName || userName || `User ${id}`);
          unameMap.set(id, userName);
          if (u.travellingUser ?? u.TravellingUser) {
            travellingIds.add(id);
          }
        });
        setTravellingUserIds(travellingIds);
        setUsersById(nameMap);
        setUsernameById(unameMap);
        const fetchNow = new Date();
        setFrozenNow(fetchNow);

        // Keep loader visible until login/task enrichment finishes —
        // otherwise empty API placeholder rows can render as 00:00:00 members.
        const startStopPromise = fetch(`${API_BASE_URL}/api/StartStop/Start_Stop_ALL`, {
          method: 'GET',
          headers: authHeaders(),
          signal: controller.signal,
        });

        const userLogsPromise = fetch(userLogsUrl, {
          method: 'GET',
          headers: authHeaders(),
          signal: controller.signal,
        });

        const [startStopRes, userLogsRes] = await Promise.all([
          startStopPromise.catch(() => null),
          userLogsPromise.catch(() => null),
        ]);

        if (controller.signal.aborted) return;

        if (startStopRes?.ok) {
          const startStopData = await startStopRes.json();
          setStartStopRecords(Array.isArray(startStopData) ? startStopData : []);
        } else {
          setStartStopRecords([]);
        }

        if (userLogsRes?.ok) {
          const userLogsData = await userLogsRes.json();
          const logs = Array.isArray(userLogsData) ? userLogsData : [];
          setUserLogs(logs);
          setFirstLoginMap(buildFirstLoginStartMap(logs, fetchNow));
        } else {
          setUserLogs([]);
          setFirstLoginMap(new Map());
        }

        setEnrichmentReady(true);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error(err);
        if (showLoading) {
          setError(err.message || 'Something went wrong');
          setStartStopRecords([]);
          setUserLogs([]);
          setFirstLoginMap(new Map());
          setRows([]);
        }
      } finally {
        if (!controller.signal.aborted && showLoading) {
          setLoading(false);
          onLoadingChange?.(false);
        }
      }
    };

    loadData(true);

    return () => {
      controller.abort();
      onLoadingChange?.(false);
    };
  }, [mode, WorkspaceID, employeeName, startDate, endDate, refreshKey, onReportRows, onLoadingChange]);

  const loginTimeMap = useMemo(
    () => buildLoginTimeMap(userLogs, frozenNow),
    [userLogs, frozenNow]
  );

  const taskTimeMap = useMemo(() => {
    const raw = buildTaskTimeMap(startStopRecords, frozenNow, userLogs);
    return capTimeMapToLogin(raw, loginTimeMap);
  }, [startStopRecords, frozenNow, userLogs, loginTimeMap]);

  const closedCallMeetingMap = useMemo(() => {
    const raw = buildClosedCallMeetingTaskTimeMap(startStopRecords, frozenNow);
    return capTimeMapToLogin(raw, loginTimeMap);
  }, [startStopRecords, frozenNow, loginTimeMap]);

  const activeCallMeetingMap = useMemo(() => {
    const raw = buildActiveCallMeetingTaskTimeMap(startStopRecords, frozenNow);
    return capTimeMapToLogin(raw, loginTimeMap);
  }, [startStopRecords, frozenNow, loginTimeMap]);

  const firstTaskStartMap = useMemo(
    () => buildFirstTaskStartMap(startStopRecords, travellingUserIds),
    [startStopRecords, travellingUserIds]
  );

  const reportRange = useMemo(
    () => getReportFetchRange(mode, startDate, endDate),
    [mode, startDate, endDate]
  );

  const hasApiActivity = (r) =>
    hmsToMs(r.totalTime ?? r.TotalTime) > 0
    || hmsToMs(r.productive ?? r.Productive) > 0
    || hmsToMs(r.neutral ?? r.Neutral) > 0
    || hmsToMs(r.clickProductive ?? r.ClickProductive) > 0;

  const dailyRowByKey = useMemo(() => {
    const { fromDate, toDate } = reportRange;
    const map = new Map();

    (rows || []).forEach((r) => {
      if (!matchesEmployeeName(r, employeeName)) return;
      const uid = r.userId ?? r.UserId;
      const wd = toInputDate(r.workDate);
      if (!uid || !wd) return;
      if (wd < fromDate || wd > toDate) return;
      // Skip empty placeholder rows (no login/task/productive time)
      if (!hasApiActivity(r)) return;
      map.set(`${uid}|${wd}`, r);
    });

    travellingUserIds.forEach((uid) => {
      const travellingUserName = usernameById.get(Number(uid)) ?? '';
      if (employeeName?.trim() && !matchesEmployeeName({ userName: travellingUserName }, employeeName)) {
        return;
      }

      taskTimeMap.forEach((ms, key) => {
        const [userIdStr, dateStr] = key.split('|');
        if (Number(userIdStr) !== Number(uid) || ms <= 0) return;
        if (dateStr < fromDate || dateStr > toDate) return;
        if (map.has(key)) return;

        const fromStop = startStopRecords.find((item) => {
          const id = Number(item.userid ?? item.Userid ?? item.userId ?? item.UserId);
          return id === Number(uid) && toInputDate(item.startTime ?? item.StartTime) === dateStr;
        });

        map.set(key, {
          userId: Number(uid),
          UserId: Number(uid),
          workDate: dateStr,
          userName: fromStop?.userName ?? fromStop?.UserName ?? usersById.get(Number(uid)) ?? `User ${uid}`,
          UserName: fromStop?.userName ?? fromStop?.UserName ?? usersById.get(Number(uid)) ?? `User ${uid}`,
          workspaceName: '',
        });
      });
    });

    return map;
  }, [rows, travellingUserIds, taskTimeMap, startStopRecords, usersById, usernameById, employeeName, reportRange]);

  const dayWiseRows = useMemo(() => {
    const { fromDate, toDate } = reportRange;
    const dailyEntries = [...dailyRowByKey.values()];

    const allowUser = (uid) => {
      if (!employeeName?.trim()) return true;
      const uname = usernameById.get(Number(uid)) ?? '';
      const displayName = usersById.get(Number(uid)) ?? '';
      return (
        matchesEmployeeName({ userName: uname }, employeeName) ||
        matchesEmployeeName({ userName: displayName }, employeeName)
      );
    };

    const byKey = new Map(
      dailyEntries.map((r) => [`${r.userId ?? r.UserId}|${toInputDate(r.workDate)}`, r])
    );
    const addFromMap = (timeMap, dateStr, options = {}) => {
      const { skipTravelling = false } = options;
      timeMap.forEach((ms, key) => {
        const [userIdStr, d] = key.split('|');
        if (d !== dateStr || ms < 1000) return; // ignore sub-second noise
        const uid = Number(userIdStr);
        if (!allowUser(uid)) return;
        if (skipTravelling && travellingUserIds.has(uid)) return;
        const k = `${uid}|${dateStr}`;
        if (byKey.has(k)) return;
        const row = {
          userId: uid,
          UserId: uid,
          userName: usersById.get(uid) ?? `User ${uid}`,
          UserName: usersById.get(uid) ?? `User ${uid}`,
          workspaceName: '',
          workDate: dateStr,
        };
        byKey.set(k, row);
      });
    };
    iterateDatesInRange(fromDate, toDate).forEach((dateStr) => {
      // Travelling users are measured by task time only — never invent rows from login map
      addFromMap(loginTimeMap, dateStr, { skipTravelling: true });
      addFromMap(taskTimeMap, dateStr);
    });

    // Keep only rows that would show real time on screen
    const withActivity = [...byKey.values()].filter((r) => {
      const uid = Number(r.userId ?? r.UserId);
      const wd = toInputDate(r.workDate);
      if (!uid || !wd) return false;
      const key = `${uid}|${wd}`;
      const isTravelling = travellingUserIds.has(uid);
      if (isTravelling) {
        return (taskTimeMap.get(key) || 0) >= 1000;
      }
      if ((loginTimeMap.get(key) || 0) >= 1000) return true;
      if ((taskTimeMap.get(key) || 0) >= 1000) return true;
      return hasApiActivity(r);
    });

    return withActivity.sort((a, b) => {
      const dateCmp = String(b.workDate).localeCompare(String(a.workDate));
      if (dateCmp !== 0) return dateCmp;
      return String(a.userName ?? a.UserName).localeCompare(String(b.userName ?? b.UserName));
    });
  }, [dailyRowByKey, loginTimeMap, taskTimeMap, travellingUserIds, usersById, usernameById, employeeName, reportRange]);

  const displayRows = useMemo(() => {
    const { fromDate, toDate } = reportRange;
    const isWeeklyRange = mode === 'weekly' && fromDate !== toDate;

    const rowHasRealActivity = (row) => {
      const uid = Number(row.userId ?? row.UserId);
      if (!uid) return false;
      const isTravelling = travellingUserIds.has(uid);

      const dates = row.isWeeklyAggregate && isWeeklyRange
        ? iterateDatesInRange(fromDate, toDate)
        : (() => {
            const wd = toInputDate(row.workDate);
            return wd ? [wd] : [];
          })();

      if (!dates.length) return false;

      let totalMs = 0;
      let taskMs = 0;
      dates.forEach((workDate) => {
        const key = `${uid}|${workDate}`;
        taskMs += taskTimeMap.get(key) || 0;
        if (isTravelling) {
          totalMs += taskTimeMap.get(key) || 0;
          return;
        }
        const dayRow = dailyRowByKey.get(key);
        if (dayRow && hasApiActivity(dayRow)) {
          totalMs += hmsToMs(dayRow.totalTime ?? dayRow.TotalTime)
            || hmsToMs(dayRow.productive ?? dayRow.Productive)
            || hmsToMs(dayRow.neutral ?? dayRow.Neutral)
            || hmsToMs(dayRow.clickProductive ?? dayRow.ClickProductive);
          return;
        }
        totalMs += loginTimeMap.get(key) || 0;
      });

      // Match what the table shows: hide rows that render as 00:00:00
      return totalMs >= 1000 || taskMs >= 1000;
    };

    if (!isWeeklyRange) {
      return dayWiseRows.filter(rowHasRealActivity);
    }

    const allowUser = (uid) => {
      if (!employeeName?.trim()) return true;
      const uname = usernameById.get(Number(uid)) ?? '';
      const displayName = usersById.get(Number(uid)) ?? '';
      return (
        matchesEmployeeName({ userName: uname }, employeeName) ||
        matchesEmployeeName({ userName: displayName }, employeeName)
      );
    };

    const userRows = new Map();
    const ensureUser = (uid, seedRow = null) => {
      if (!userRows.has(uid)) {
        userRows.set(uid, {
          userId: uid,
          UserId: uid,
          userName: seedRow?.userName ?? seedRow?.UserName ?? usersById.get(uid) ?? `User ${uid}`,
          UserName: seedRow?.userName ?? seedRow?.UserName ?? usersById.get(uid) ?? `User ${uid}`,
          workspaceName: seedRow?.workspaceName ?? seedRow?.WorkspaceName ?? '',
          workDate: fromDate,
          rangeEndDate: toDate,
          isWeeklyAggregate: true,
        });
      } else if (seedRow) {
        const existing = userRows.get(uid);
        if (!existing.workspaceName && (seedRow.workspaceName ?? seedRow.WorkspaceName)) {
          existing.workspaceName = seedRow.workspaceName ?? seedRow.WorkspaceName;
        }
      }
    };

    dayWiseRows.forEach((row) => {
      ensureUser(row.userId ?? row.UserId, row);
    });

    iterateDatesInRange(fromDate, toDate).forEach((dateStr) => {
      loginTimeMap.forEach((ms, key) => {
        const [userIdStr, d] = key.split('|');
        if (d !== dateStr || ms < 1000) return;
        const uid = Number(userIdStr);
        if (!allowUser(uid)) return;
        if (travellingUserIds.has(uid)) return;
        ensureUser(uid, { userName: usersById.get(uid) });
      });
      taskTimeMap.forEach((ms, key) => {
        const [userIdStr, d] = key.split('|');
        if (d !== dateStr || ms < 1000) return;
        const uid = Number(userIdStr);
        if (!allowUser(uid)) return;
        ensureUser(uid, { userName: usersById.get(uid) });
      });
    });

    return [...userRows.values()]
      .filter(rowHasRealActivity)
      .sort((a, b) =>
        String(a.userName ?? a.UserName).localeCompare(String(b.userName ?? b.UserName))
      );
  }, [dayWiseRows, loginTimeMap, taskTimeMap, dailyRowByKey, travellingUserIds, usersById, usernameById, employeeName, mode, reportRange]);

  const isTravellingUser = (userId) => travellingUserIds.has(Number(userId));

  const getDatesForRow = (row) => {
    const { fromDate, toDate } = reportRange;
    if (row.isWeeklyAggregate && mode === 'weekly' && fromDate !== toDate) {
      return iterateDatesInRange(fromDate, toDate);
    }
    const wd = toInputDate(row.workDate);
    return wd ? [wd] : [];
  };

  /** Prefer API report times (SP) over client-side login recalculation. */
  const getApiDayTimesMs = (userId, workDate) => {
    const dayRow = dailyRowByKey.get(`${userId}|${workDate}`);
    if (!dayRow) return null;

    const totalMs = hmsToMs(dayRow.totalTime ?? dayRow.TotalTime);
    const productiveMs = hmsToMs(dayRow.productive ?? dayRow.Productive);
    const neutralMs = hmsToMs(dayRow.neutral ?? dayRow.Neutral);
    const clickMs = hmsToMs(dayRow.clickProductive ?? dayRow.ClickProductive);

    if (totalMs <= 0 && productiveMs <= 0 && neutralMs <= 0 && clickMs <= 0) {
      return null;
    }

    const resolvedProductive = productiveMs > 0 ? productiveMs : clickMs;
    const resolvedNeutral = neutralMs > 0
      ? neutralMs
      : Math.max(0, totalMs - resolvedProductive);

    return {
      totalMs: totalMs > 0 ? totalMs : resolvedProductive + resolvedNeutral,
      productiveMs: resolvedProductive,
      neutralMs: resolvedNeutral,
    };
  };

  const getRowTimeMs = (row) => {
    if (!enrichmentReady) {
      return {
        productiveMs: hmsToMs(row.productive ?? row.Productive),
        neutralMs: hmsToMs(row.neutral ?? row.Neutral),
      };
    }

    const userId = row.userId ?? row.UserId;
    if (!userId) return { productiveMs: 0, neutralMs: 0 };

    const dates = getDatesForRow(row);
    if (!dates.length) return { productiveMs: 0, neutralMs: 0 };

    if (row.isWeeklyAggregate) {
      let productiveMs = 0;
      let neutralMs = 0;

      dates.forEach((workDate) => {
        const dayKey = `${userId}|${workDate}`;
        if (isTravellingUser(userId)) {
          productiveMs += taskTimeMap.get(dayKey) || 0;
          return;
        }

        const apiTimes = getApiDayTimesMs(userId, workDate);
        if (apiTimes) {
          productiveMs += apiTimes.productiveMs;
          neutralMs += apiTimes.neutralMs;
          return;
        }

        const dayRow = dailyRowByKey.get(dayKey) || row;
        const times = computeRowProductivityMs({
          totalMs: loginTimeMap.get(dayKey) || 0,
          row: dayRow,
          startStopRecords,
          userId,
          workDate,
          closedCallMeetingMap,
          activeCallMeetingMap,
          referenceNow: frozenNow,
          meetingNeutralLockRef: meetingNeutralLockRef.current,
          wasCallMeetingActiveRef: wasCallMeetingActiveRef.current,
          neutralMaxRef: neutralMaxRef.current,
        });
        productiveMs += times.productiveMs;
        neutralMs += times.neutralMs;
      });

      return { productiveMs, neutralMs };
    }

    const workDate = toInputDate(row.workDate);
    if (!workDate) return { productiveMs: 0, neutralMs: 0 };

    if (isTravellingUser(userId)) {
      const taskMs = taskTimeMap.get(`${userId}|${workDate}`) || 0;
      return { productiveMs: taskMs, neutralMs: 0 };
    }

    const apiTimes = getApiDayTimesMs(userId, workDate);
    if (apiTimes) {
      return {
        productiveMs: apiTimes.productiveMs,
        neutralMs: apiTimes.neutralMs,
      };
    }

    return computeRowProductivityMs({
      totalMs: loginTimeMap.get(`${userId}|${workDate}`) || 0,
      row,
      startStopRecords,
      userId,
      workDate,
      closedCallMeetingMap,
      activeCallMeetingMap,
      referenceNow: frozenNow,
      meetingNeutralLockRef: meetingNeutralLockRef.current,
      wasCallMeetingActiveRef: wasCallMeetingActiveRef.current,
      neutralMaxRef: neutralMaxRef.current,
    });
  };

  const getProductiveMsForRow = (row) => getRowTimeMs(row).productiveMs;

  const getTotalTimeForRow = (row) => {
    if (!enrichmentReady) {
      return row.totalTime ?? row.TotalTime ?? '00:00:00';
    }

    const userId = row.userId ?? row.UserId;
    if (!userId) return '00:00:00';

    const dates = getDatesForRow(row);
    if (!dates.length) return '00:00:00';

    let totalMs = 0;
    dates.forEach((workDate) => {
      const dayKey = `${userId}|${workDate}`;
      if (isTravellingUser(userId)) {
        totalMs += taskTimeMap.get(dayKey) || 0;
        return;
      }

      const apiTimes = getApiDayTimesMs(userId, workDate);
      if (apiTimes) {
        totalMs += apiTimes.totalMs;
        return;
      }

      totalMs += loginTimeMap.get(dayKey) || 0;
    });

    return formatDurationMs(totalMs);
  };

  const getProductiveTimeForRow = (row) => {
    if (!enrichmentReady) {
      return row.productive ?? row.Productive ?? '00:00:00';
    }
    return formatDurationMs(getProductiveMsForRow(row));
  };

  const getNeutralTimeForRow = (row) => {
    if (!enrichmentReady) {
      return row.neutral ?? row.Neutral ?? '00:00:00';
    }
    return formatDurationMs(getRowTimeMs(row).neutralMs);
  };

  const getTaskTimeForRow = (row) => {
    if (!enrichmentReady) {
      return '—';
    }

    const userId = row.userId ?? row.UserId;
    if (!userId) return '00:00:00';

    const dates = getDatesForRow(row);
    if (!dates.length) return '00:00:00';

    const rangeStart = dates[0];
    const rangeEnd = dates[dates.length - 1];
    const totalMinutes = sumUserTaskMinutesForDisplay(
      startStopRecords,
      userId,
      rangeStart,
      rangeEnd,
      frozenNow
    );

    return formatDurationMs(totalMinutes * 60000);
  };

  const isZeroDuration = (value) => {
    if (value == null || value === '' || value === '—') return true;
    const text = String(value).trim();
    if (text === '00:00:00' || text === '0:00:00') return true;
    return hmsToMs(text) < 1000;
  };

  /** Final gate: never show members whose on-screen times are all zero. */
  const visibleRows = useMemo(
    () =>
      displayRows.filter((r) => {
        const total = getTotalTimeForRow(r);
        const task = getTaskTimeForRow(r);
        const productive = getProductiveTimeForRow(r);
        const neutral = getNeutralTimeForRow(r);
        return !(
          isZeroDuration(total)
          && isZeroDuration(task)
          && isZeroDuration(productive)
          && isZeroDuration(neutral)
        );
      }),
    // getters close over current enrichment maps/rows
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayRows, enrichmentReady, loginTimeMap, taskTimeMap, dailyRowByKey, startStopRecords, frozenNow, travellingUserIds]
  );

  useEffect(() => {
    if (!onReportRows || !enrichmentReady) return;

    onReportRows(
      visibleRows.map((r) => ({
        productive: getProductiveTimeForRow(r),
        neutral: getNeutralTimeForRow(r),
      }))
    );
  }, [
    visibleRows,
    enrichmentReady,
    onReportRows,
    rows,
    startStopRecords,
    userLogs,
    loginTimeMap,
    taskTimeMap,
    closedCallMeetingMap,
    activeCallMeetingMap,
    frozenNow,
    travellingUserIds,
    dailyRowByKey,
    mode,
    reportRange,
  ]);

  const getStartTimeForRow = (row) => {
    if (!enrichmentReady) {
      return '—';
    }

    const userId = row.userId ?? row.UserId;
    if (!userId) return '—';

    const { toDate } = reportRange;
    if (!toDate) return '—';

    if (isTravellingUser(userId)) {
      const raw = firstTaskStartMap.get(`${userId}|${toDate}`);
      if (!raw || !isOnOrAfter7AmIst(raw, toDate)) return '—';
      return formatLoginStartTime(raw);
    }

    const earliestMs = getEarliestLoginOnDateAfter7Am(userLogs, userId, toDate);
    return earliestMs != null ? formatLoginStartTime(earliestMs) : '—';
  };

  const isWeeklyDateRangeRow = (row) => {
    const { fromDate, toDate } = reportRange;
    return row.isWeeklyAggregate && mode === 'weekly' && fromDate !== toDate;
  };

  const getRowDateLabel = (row) => {
    const { fromDate, toDate } = reportRange;
    if (isWeeklyDateRangeRow(row)) {
      return `${toShortDisplayDate(fromDate)} to ${toShortDisplayDate(toDate)}`;
    }
    return toShortDisplayDate(row.workDate);
  };

  const renderRowDateCell = (row) => {
    const { fromDate, toDate } = reportRange;
    if (isWeeklyDateRangeRow(row)) {
      return (
        <span className="activity-date-range">
          <span className="activity-date-range-line">
            {toShortDisplayDate(fromDate)} to
          </span>
          <span className="activity-date-range-line">
            {toShortDisplayDate(toDate)}
          </span>
        </span>
      );
    }
    return toShortDisplayDate(row.workDate);
  };

  const openLoginModal = (row) => {
    const userId = row.userId ?? row.UserId;
    if (!userId) return;
    const { startDate: loginStart, endDate: loginEnd } = getLoginDateRange(mode, startDate, endDate, row.workDate);
    setLoginModal({
      userId,
      userName: row.userName ?? row.UserName ?? '',
      startDate: loginStart,
      endDate: loginEnd,
    });
  };

  const buildExportRows = (sourceRows) =>
    sourceRows.map((r) => {
      const workspaceLabel = r.workspaceName ?? r.WorkspaceName ?? '';
      const teams = workspaceLabel
        ? workspaceLabel.split(',').map((name) => name.trim()).join(', ')
        : 'Unassigned';
      return {
        date: getRowDateLabel(r),
        member: r.userName ?? r.UserName ?? '',
        teams,
        startTime: getStartTimeForRow(r),
        totalTime: getTotalTimeForRow(r),
        taskTime: getTaskTimeForRow(r),
        productive: getProductiveTimeForRow(r),
        neutral: getNeutralTimeForRow(r),
      };
    });

  const handleExportPdf = () => {
    exportActivityTableToPdf(buildExportRows(visibleRows));
  };

  const ACTIVITY_EXPORT_HEADERS = [
    'Date',
    'Member',
    'Teams',
    'Start Time',
    'Total Logged in Time',
    'Total Task Time',
    'Productive Time',
    'Neutral Time',
  ];

  // Always day-wise (one row per employee per day), even when UI is weekly-aggregated.
  const buildActivityExportRows = () =>
    buildExportRows(
      dayWiseRows.filter((r) => {
        const total = getTotalTimeForRow(r);
        const task = getTaskTimeForRow(r);
        return !isZeroDuration(total) || !isZeroDuration(task);
      })
    ).map((row) => [
      row.date,
      row.member,
      row.teams,
      row.startTime,
      row.totalTime,
      row.taskTime,
      row.productive,
      row.neutral,
    ]);

  const handleExportCsv = () => {
    downloadCsv(
      `monthly-activity-report-${stampFileDate()}.csv`,
      ACTIVITY_EXPORT_HEADERS,
      buildActivityExportRows()
    );
  };

  const handleExportExcel = () => {
    downloadExcel(
      `monthly-activity-report-${stampFileDate()}.xls`,
      ACTIVITY_EXPORT_HEADERS,
      buildActivityExportRows()
    );
  };

  const renderHeader = () => (
    <div className="activity-table-header d-flex justify-content-between align-items-center mb-3">
      <h5 className="fw-bold mb-0">Activity</h5>
      <div className="d-flex gap-2">
        <button
          type="button"
          className="btn btn-outline-success btn-sm"
          onClick={handleExportExcel}
          disabled={visibleRows.length === 0}
          title="Day-wise Excel (one row per employee per day)"
        >
          <i className="fas fa-file-excel me-1" aria-hidden="true" />
          Export Excel
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={handleExportCsv}
          disabled={visibleRows.length === 0}
          title="Day-wise CSV (one row per employee per day)"
        >
          <i className="fas fa-file-csv me-1" aria-hidden="true" />
          Export CSV
        </button>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={handleExportPdf}
          disabled={visibleRows.length === 0}
        >
          <i className="fas fa-file-pdf me-1" aria-hidden="true" />
          Export to PDF
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        {renderHeader()}
        <PageLoader label="Loading activity..." />
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderHeader()}
        <div className="table-responsive">
          <div className="p-3 text-center text-danger">Error: {error}</div>
        </div>
      </>
    );
  }

  return (
    <>
      {renderHeader()}
      <div className="table-responsive">
        <table className="table table-custom table-hover">
          <thead>
            <tr>
              <th>Date</th>
              <th>Member</th>
              <th>Teams</th>
              <th>Start Time</th>
              <th>Total time</th>
              <th>Task Time</th>
              <th>Productive</th>
              <th>Neutral</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-muted py-3">No records found.</td>
              </tr>
            )}
            {visibleRows.map((r) => {
              const totalTime = getTotalTimeForRow(r);
              const workspaceLabel = r.workspaceName ?? r.WorkspaceName ?? '';
              const rowTimeMs = getRowTimeMs(r);
              const rowUserId = r.userId ?? r.UserId;
              return (
              <tr key={`${rowUserId}-${r.workDate}-${r.rangeEndDate ?? ''}-${r.workspaceID ?? 'na'}`}>
                <td className="text-muted activity-date-cell">{renderRowDateCell(r)}</td>
                <td className="member-cell">
                  <div className="employee-info">
                    <UserAvatar
                      userId={rowUserId}
                      userName={r.userName}
                    />
                    <div className="emp-text">
                      <h6>{r.userName}</h6>
                      <ProductivityRatioBar
                        productiveMs={rowTimeMs.productiveMs}
                        neutralMs={rowTimeMs.neutralMs}
                      />
                    </div>
                  </div>
                </td>
                <td>
                  {workspaceLabel
                    ? workspaceLabel.split(',').map((name, index) => (
                        <span key={index} className="tag me-1">
                          {name.trim()}
                        </span>
                      ))
                    : <span className="tag">Unassigned</span>}
                </td>
                <td className="text-muted">{getStartTimeForRow(r)}</td>
                <td>{getTotalTimeForRow(r)}</td>
                <td>{getTaskTimeForRow(r)}</td>
                <td><div className="text-green">{getProductiveTimeForRow(r)}</div></td>
                <td><div className="text-neutral">{getNeutralTimeForRow(r)}</div></td>
                <td>
                  <div className="dashboard-row-actions">
                    <i
                      className="far fa-eye text-muted"
                      title="View activity"
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        navigate(`/ViewActivityHistory/${r.userId}`, {
                          state: {
                            totalTime,
                            workDate: r.workDate,
                            endDate: r.rangeEndDate || r.workDate,
                            reportMode: mode,
                          },
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          navigate(`/ViewActivityHistory/${r.userId}`, {
                            state: {
                              totalTime,
                              workDate: r.workDate,
                              endDate: r.rangeEndDate || r.workDate,
                              reportMode: mode,
                            },
                          });
                        }
                      }}
                    />
                    <i
                      className="fas fa-user-clock text-muted"
                      title="Login history"
                      role="button"
                      tabIndex={0}
                      onClick={() => openLoginModal(r)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') openLoginModal(r);
                      }}
                    />
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      <UserLoginHistoryModal
        userId={loginModal?.userId ?? null}
        userName={loginModal?.userName ?? ''}
        startDate={loginModal?.startDate ?? ''}
        endDate={loginModal?.endDate ?? ''}
        onClose={() => setLoginModal(null)}
      />
    </>
  );
}
